/**
 * Central API module
 * Exports all API namespaces with consistent error handling and loading states
 */

// Import Supabase client
import { supabase } from '../lib/supabase';

// Import all API modules
import { authAPI } from './auth';
import { reservationAPI } from './reservations';
import { venueAPI } from './venues';
import { teamAPI } from './teams';
import { tournamentAPI } from './tournaments';
import { gameAPI } from './games';
import { userAPI } from './users';
import { contactAPI } from './contact';
import { paymentAPI, adminPaymentAPI } from './payments';
import { auditLogAPI } from './auditLog';
import { scheduleImportAPI } from './scheduleImport';
import { teamAliasAPI } from './teamAliases';

// Import base utilities
import { 
  handleAPICall, 
  createAPIMethod, 
  createPaginatedAPIMethod,
  batchAPICall,
  retryAPICall 
} from './base';

/**
 * Central API object with all available operations
 */
export const API = {
  auth: authAPI,
  reservations: reservationAPI,
  venues: venueAPI,
  teams: teamAPI,
  tournaments: tournamentAPI,
  games: gameAPI,
  users: userAPI,
  contact: contactAPI,
  payments: paymentAPI,
  adminPayments: adminPaymentAPI,
  auditLog: auditLogAPI,
  scheduleImport: scheduleImportAPI,
  teamAliases: teamAliasAPI
};

/**
 * API utilities for custom implementations
 */
export const APIUtils = {
  handleAPICall,
  createAPIMethod,
  createPaginatedAPIMethod,
  batchAPICall,
  retryAPICall
};

/**
 * Convenience method to check API health
 * @returns {Promise<APIResponse>}
 */
export const checkAPIHealth = createAPIMethod(
  async () => {
    const startTime = Date.now();
    
    // Test basic database connectivity
    const { error } = await supabase
      .from('users')
      .select('count')
      .limit(1)
      .single();
    
    const responseTime = Date.now() - startTime;
    
    return {
      status: 'healthy',
      responseTime,
      timestamp: new Date().toISOString(),
      database: error ? 'error' : 'connected'
    };
  },
  { operation: 'API Health Check' }
);

// Export individual APIs for direct imports
export { authAPI } from './auth';
export { reservationAPI } from './reservations';
export { venueAPI } from './venues';
export { teamAPI } from './teams';
export { tournamentAPI } from './tournaments';
export { gameAPI } from './games';
export { userAPI } from './users';
export { contactAPI } from './contact';
export { paymentAPI, adminPaymentAPI } from './payments';
export { auditLogAPI } from './auditLog';
export { scheduleImportAPI } from './scheduleImport';
export { teamAliasAPI } from './teamAliases';

// Default export
export default API;